import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingCart, Heart, User, Home, Grid3x3, ClipboardList, LogOut, MessageCircle, X, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { useStorefrontConfig } from "@/context/StorefrontConfigContext";
import { AnnouncementBar } from "@/components/storefront/AnnouncementBar";
import { PremiumFooter } from "@/components/storefront/PremiumFooter";
import { CompanyNav } from "@/components/company/CompanyNav";

const surfaceCls = "bg-[color:var(--brand-surface)] border-[color:var(--brand-border)]";

function Header({ onSearch, searchValue, setSearchValue }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const { config } = useStorefrontConfig();
  const headerStyle = config.design.header_style;
  const showSearch = headerStyle !== "simple" && headerStyle !== "centered-logo";
  const centered = headerStyle === "centered-logo";
  const premium = headerStyle === "premium-compact";
  const [openMenu, setOpenMenu] = useState(false);
  const [navCats, setNavCats] = useState([]);
  useEffect(() => {
    if (!premium) return;
    api.get("/categories").then((r) => setNavCats((r.data || []).slice(0, 8))).catch(() => {});
  }, [premium]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/katalog?search=${encodeURIComponent(searchValue.trim())}`);
    }
  };

  return (
    <header className={`storefront-header sticky top-0 z-40 border-b ${surfaceCls}`} data-header-style={headerStyle}>
      {premium ? <AnnouncementBar /> : null}
      <div className={`storefront-container py-2.5 sm:py-3 ${centered ? "" : ""}`}>
        <div className="flex min-h-12 items-center gap-3 md:gap-6">
          <div className="min-w-0 flex-1 md:flex-none md:min-w-[160px] md:shrink-0">
            <Logo variant="compact" size="md" className="storefront-header-logo shrink-0" />
          </div>

          {showSearch ? (
            <form onSubmit={onSubmit} className="hidden md:block flex-1 max-w-lg mx-auto" role="search">
              <div className="storefront-search relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--brand-muted)]" size={18} />
                <Input
                  data-testid="header-search-input"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Cari produk di toko ini..."
                  className="pl-11 pr-4 h-11 rounded-[var(--storefront-control-radius)] bg-[color:var(--color-surface-secondary)] border-[color:var(--color-border)] text-[color:var(--color-text-primary)] focus-visible:brand-ring-primary"
                />
              </div>
            </form>
          ) : (
            <div className="hidden md:block flex-1" />
          )}

          <div className="flex shrink-0 items-center gap-0.5 sm:gap-1.5">
          <Link to="/wishlist" className="hidden sm:block" aria-label="Wishlist">
            <Button variant="ghost" size="icon" className="storefront-icon-button" data-testid="header-wishlist-btn">
              <Heart size={20} />
            </Button>
          </Link>
          <Link to="/cart" aria-label="Keranjang belanja">
            <Button variant="ghost" size="icon" className="storefront-icon-button relative" data-testid="header-cart-btn">
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[color:var(--nav-action)] text-[color:var(--nav-action-fg)] text-[10px] font-bold rounded-full min-w-[18px] h-[18px] grid place-items-center px-1 ring-2 ring-[color:var(--brand-surface)]">
                  {cartCount}
                </span>
              )}
            </Button>
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="storefront-account-button gap-2 px-1.5 sm:px-3" data-testid="header-user-menu">
                  <div className="w-7 h-7 rounded-full nav-avatar grid place-items-center font-bold text-xs">
                    {(user.name || user.email)[0].toUpperCase()}
                  </div>
                  <span className="hidden md:block text-sm font-semibold">{user.name?.split(" ")[0] || "Akun"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User className="mr-2" size={16} /> Profil Saya
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/pesanan")} data-testid="menu-orders">
                  <ClipboardList className="mr-2" size={16} /> Pesanan Saya
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/wishlist")} data-testid="menu-wishlist">
                  <Heart className="mr-2" size={16} /> Wishlist
                </DropdownMenuItem>
                {(user.role === "admin" || user.role === "owner") && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <Grid3x3 className="mr-2" size={16} /> Admin Panel
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="mr-2" size={16} /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" className="hidden sm:block">
              {/* Design System v2 (Phase 2) — Primary CTA: color-primary + hover
                  color-primary-dark. Sits in the tenant-scoped header so the
                  active tenant palette drives it. */}
              <Button className="color-btn-primary h-10 px-5 font-bold rounded-[var(--brand-radius)]" data-testid="header-login-btn">
                Masuk
              </Button>
            </Link>
          )}
          </div>
        </div>

        {showSearch ? (
          <form onSubmit={onSubmit} className="mt-2.5 md:hidden" role="search">
            <div className="storefront-search relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[color:var(--brand-muted)]" size={17} />
              <Input
                data-testid="header-search-input-mobile"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Cari produk..."
                className="h-11 pl-10 pr-4 rounded-[var(--storefront-control-radius)] bg-[color:var(--color-surface-secondary)] border-[color:var(--color-border)] text-[color:var(--color-text-primary)] focus-visible:brand-ring-primary"
              />
            </div>
          </form>
        ) : null}
      </div>
      {premium && navCats.length > 0 ? (
        <nav className="gcp-nav-strip hidden md:block border-t border-[color:var(--color-border)]" aria-label="Kategori" data-testid="header-category-strip">
          <div className="storefront-container flex items-center gap-1 overflow-x-auto no-scrollbar">
            <Link to="/katalog" className="gcp-nav-link" data-testid="nav-strip-all">Semua Produk</Link>
            {navCats.map((c) => (
              <Link key={c.slug} to={`/katalog?category=${c.slug}`} className="gcp-nav-link" data-testid={`nav-strip-${c.slug}`}>{c.name}</Link>
            ))}
          </div>
        </nav>
      ) : null}
      {/* Company Profile ON → nav section CP (anchor) + Toko → /katalog; OFF → tidak dirender (header existing). */}
      <CompanyNav />
    </header>
  );
}

function BottomNav() {
  const location = useLocation();
  const { cartCount } = useCart();
  const items = [
    { to: "/", icon: Home, label: "Beranda" },
    { to: "/katalog", icon: Grid3x3, label: "Katalog" },
    { to: "/cart", icon: ShoppingCart, label: "Keranjang", badge: cartCount },
    { to: "/pesanan", icon: ClipboardList, label: "Pesanan" },
    { to: "/profile", icon: User, label: "Akun" },
  ];
  return (
    <nav className={`storefront-bottom-nav fixed bottom-0 left-0 right-0 z-40 border-t md:hidden ${surfaceCls}`} aria-label="Navigasi utama mobile">
      <div className="flex justify-around items-stretch px-1 pt-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))]">
        {items.map((it) => {
          const active = location.pathname === it.to || (it.to !== "/" && location.pathname.startsWith(it.to));
          const Icon = it.icon;
          return (
            <Link
              key={it.to}
              to={it.to}
              data-testid={`bottomnav-${it.label.toLowerCase()}`}
              className={`relative flex-1 flex flex-col items-center gap-1 rounded-xl py-2 transition-colors ${
                active ? "text-[color:var(--color-primary)]" : "text-[color:var(--color-text-secondary)]"
              }`}
            >
              <div className="relative">
                <Icon size={20} strokeWidth={active ? 2.5 : 2} />
                {it.badge > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 bg-[color:var(--nav-action)] text-[color:var(--nav-action-fg)] text-[9px] font-bold rounded-full min-w-[16px] h-[16px] grid place-items-center px-1">
                    {it.badge}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-semibold tracking-[-0.01em]">{it.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function WhatsAppButton() {
  const { branding } = useBranding();
  const phone = branding.contact.whatsapp;
  if (!phone) return null;
  const brandName = branding.general.name;
  return (
    <a
      href={`https://wa.me/${phone}?text=${encodeURIComponent(`Halo ${brandName}, saya ingin tanya tentang produk.`)}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float-btn"
      /* intentional: #25D366 is the WhatsApp brand color — a third-party
         product identity, must remain constant across all tenants. */
      className="fixed right-4 bottom-20 md:bottom-6 z-30 w-12 h-12 rounded-full bg-[#25D366] text-white grid place-items-center shadow-[0_10px_28px_rgba(37,211,102,0.28)] hover:-translate-y-0.5 transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#25D366]"
      aria-label={`Hubungi ${brandName} melalui WhatsApp`}
    >
      <MessageCircle size={22} fill="white" />
    </a>
  );
}

function Footer() {
  const { branding } = useBranding();
  const { config } = useStorefrontConfig();
  const footerStyle = config.design.footer_style;
  const [footerCats, setFooterCats] = useState([]);
  useEffect(() => {
    // Kategori dinamis dari tenant (bukan hardcode kategori telur).
    api.get("/categories").then((r) => setFooterCats((r.data || []).slice(0, 3))).catch(() => {});
  }, []);
  const wa = branding.contact.whatsapp;
  const address = branding.contact.address;
  const copyright = branding.footer.copyright || branding.general.name;
  const poweredBy = branding.footer.powered_by;
  const compact = footerStyle === "minimal" || footerStyle === "centered";
  const mutedText = "text-[color:var(--brand-muted)]";
  if (footerStyle === "premium-compact") {
    return <PremiumFooter branding={branding} copyright={copyright} poweredBy={poweredBy} />;
  }
  return (
    <footer className={`storefront-footer mt-16 border-t ${surfaceCls}`} data-footer-style={footerStyle}>
      {compact ? (
        <div className="max-w-7xl mx-auto px-6 py-10 text-center space-y-3">
          <div className="flex justify-center"><Logo variant="compact" size="lg" to={null} /></div>
          <p className={`text-sm ${mutedText} max-w-md mx-auto leading-relaxed`}>
            {branding.footer.text || branding.general.description || branding.general.tagline}
          </p>
          <div className={`flex justify-center flex-wrap gap-4 text-sm ${mutedText}`}>
            {wa && <a href={`https://wa.me/${wa}`} className="hover:text-[color:var(--brand-primary)]">WhatsApp</a>}
            {branding.contact.email && <a href={`mailto:${branding.contact.email}`} className="hover:text-[color:var(--brand-primary)]">Email</a>}
            <Link to="/katalog" className="hover:text-[color:var(--brand-primary)]">Katalog</Link>
            <Link to="/pesanan" className="hover:text-[color:var(--brand-primary)]">Lacak Pesanan</Link>
          </div>
        </div>
      ) : (
        <div className={`max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 gap-8 ${footerStyle === "columns-2" ? "md:grid-cols-2" : "md:grid-cols-4"}`}>
          <div className={footerStyle === "columns-2" ? "" : "md:col-span-2"}>
            <Logo variant="compact" size="lg" to={null} />
            <p className={`text-sm ${mutedText} max-w-md leading-relaxed mt-3`}>
              {branding.footer.text || branding.general.description || branding.general.tagline}
            </p>
            {address && <div className={`mt-4 text-sm ${mutedText}`}>{address}</div>}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--brand-primary)] font-bold mb-3">Belanja</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/katalog" className="hover:text-[color:var(--brand-primary)]">Semua Produk</Link></li>
              {footerCats.map((c) => (
                <li key={c.slug}>
                  <Link to={`/katalog?category=${c.slug}`} className="hover:text-[color:var(--brand-primary)]">{c.name}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--brand-primary)] font-bold mb-3">Bantuan</div>
            <ul className="space-y-2 text-sm">
              {wa && (
                <li><a href={`https://wa.me/${wa}`} className="hover:text-[color:var(--brand-primary)]">WhatsApp: +{wa}</a></li>
              )}
              {branding.contact.email && (
                <li><a href={`mailto:${branding.contact.email}`} className="hover:text-[color:var(--brand-primary)]">Email: {branding.contact.email}</a></li>
              )}
              <li><Link to="/login" className="hover:text-[color:var(--brand-primary)]">Masuk / Daftar</Link></li>
              <li><Link to="/pesanan" className="hover:text-[color:var(--brand-primary)]">Lacak Pesanan</Link></li>
            </ul>
          </div>
        </div>
      )}
      <div className={`border-t border-[color:var(--brand-border)] py-4 text-center text-xs ${mutedText}`} data-testid="footer-copyright">
        © {new Date().getFullYear()} {copyright}. All rights reserved.
        {poweredBy && <span className="ml-2 opacity-75">· Powered by {poweredBy}</span>}
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const [searchValue, setSearchValue] = useState("");
  const { config } = useStorefrontConfig();
  // Design System v2 (Phase 2) — storefront root consumes canonical tokens:
  //   background → --color-background
  //   text       → --color-text-primary
  return (
    <div
      className="storefront-shell min-h-screen flex flex-col"
      data-template-layout={config.design.homepage_layout}
      style={{
        backgroundColor: "var(--color-background)",
        color: "var(--color-text-primary)",
      }}
    >
      <Header searchValue={searchValue} setSearchValue={setSearchValue} />
      <main className="flex-1 pb-24 md:pb-0">{children}</main>
      <Footer />
      <BottomNav />
      <WhatsAppButton />
    </div>
  );
}
